import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronRight, ClipboardList, Loader2 } from 'lucide-react';

import { cn } from '../lib/utils';
import { ScopeGenerationResponse, ScopeResponseLineItem } from '../types';
import type { AdapterReport } from '../types';
import { BackendAPI } from '../lib/api';
import { TraceAPI } from '../lib/traceApi';
import { PropagationReviewsAPI } from '../lib/propagationReviewsApi';
import DownloadEsxButton from '../components/DownloadEsxButton';
import type { RuleTraceEntry, RuleTraceResponse } from '../types/trace';
import EstimateRuleTracePanel from '../components/rules/EstimateRuleTracePanel';

import {
  LineItemStatus,
  SummaryStatsBar,
  QuestionsSection,
  WarningsSection,
  ValidationPanel,
  CodeRequirementsPanel,
  RoomSection,
  ScopeLineItemRow,
} from '../components/ScopeReviewComponents';

// ================================================================= //
//          Scope Review Page — Read-Only Dual Mode                    //
// ================================================================= //
// Mode A (immediate): scopeData prop passed after generation.         //
// Mode B (project):   ?project=<id> URL param loads saved scope.      //
// Accept / Reject / Edit tint every line locally; on a propagation     //
// candidate they also POST to the scope's propagation_reviews ledger.  //
// ================================================================= //

interface ScopeReviewPageProps {
  scopeData?: ScopeGenerationResponse;
}

export default function ScopeReviewPage({ scopeData }: ScopeReviewPageProps) {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('project');

  const [fetchedScope, setFetchedScope] = useState<ScopeGenerationResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [activePanel, setActivePanel] = useState<'validation' | 'codes' | null>(null);
  const [showSources, setShowSources] = useState(false);
  const [trace, setTrace] = useState<RuleTraceResponse | null>(null);
  const [traceLoading, setTraceLoading] = useState(false);
  const [traceError, setTraceError] = useState<string | null>(null);

  // ESX export state.
  const [scopeId, setScopeId] = useState<string | null>(null);
  const [saveBanner, setSaveBanner] = useState<string | null>(null);
  const [esxResult, setEsxResult] = useState<{ filename: string; report: AdapterReport } | null>(null);
  const [esxError, setEsxError] = useState<string | null>(null);

  // Reason: Mode B — fetch saved scope from project when URL has ?project=<id>
  // and no immediate prop data is provided. Also fetch the persisted rule trace
  // for that scope (PRP-003).
  useEffect(() => {
    if (projectId && !scopeData) {
      setLoading(true);
      setFetchError(null);
      BackendAPI.getLatestProjectScope(projectId)
        .then((result) => {
          if (result && result.scope_data) {
            setFetchedScope(result.scope_data as unknown as ScopeGenerationResponse);
            // Fire off the trace fetch in parallel with rendering the scope.
            const loadedScopeId = result.id as string | undefined;
            setScopeId(loadedScopeId ?? null);
            if (loadedScopeId) {
              setTraceLoading(true);
              TraceAPI.getForScope(projectId, loadedScopeId)
                .then((t) => setTrace(t))
                .catch((err) => {
                  console.error('[ScopeReview] failed to load rule trace:', err);
                  setTraceError('Could not load rule trace.');
                })
                .finally(() => setTraceLoading(false));
            }
          } else {
            setFetchedScope(null);
          }
        })
        .catch((err) => {
          console.error('[ScopeReview] Failed to load project scope:', err);
          setFetchError('Failed to load scope data.');
          setFetchedScope(null);
        })
        .finally(() => setLoading(false));
    }
  }, [projectId, scopeData]);

  // Mode A — assemble a RuleTraceResponse from the inline rule_trace[] on the
  // generation response. No extra fetch needed because the trace was emitted
  // alongside the scope itself.
  useEffect(() => {
    if (scopeData && scopeData.rule_trace !== undefined) {
      const inline = scopeData.rule_trace || [];
      const traces: RuleTraceEntry[] = inline.map((e, idx) => ({
        id: `inline-${idx}`,
        rule_id: String(e.rule_id || ''),
        rule_version: Number(e.rule_version || 1),
        rule_name: String(e.rule_name_snapshot || e.rule_name || ''),
        rule_type: (e.rule_type_snapshot || e.rule_type || 'trigger') as RuleTraceEntry['rule_type'],
        rule_severity: (e.rule_severity_snapshot || e.rule_severity || 'should') as RuleTraceEntry['rule_severity'],
        evaluation_phase: (e.evaluation_phase || 'global') as RuleTraceEntry['evaluation_phase'],
        target_item_id: (e.target_item_id as string | null) ?? null,
        fired_at: String(e.fired_at || ''),
        conditions_matched: (e.conditions_matched as Record<string, unknown>) || {},
        actions_emitted: (e.actions_emitted as Array<Record<string, unknown>>) || [],
        rationale_text: (e.rationale_text as string | null) ?? null,
      }));
      setTrace({
        scope_id: '',
        project_id: projectId || '',
        trace_count: traces.length,
        traces,
        pre_phase3: false,
      });
    }
  }, [scopeData, projectId]);

  // Use prop data first, then fetched data, then empty state
  const data = scopeData || fetchedScope || getEmptyData();
  const hasData = data.rooms.length > 0 || data.general_conditions.length > 0;

  // T-0403 criterion 5 — the Accept / Reject / Edit buttons on every line have
  // been drawn since this page shipped and were handed a dead `() => {}`. They
  // now write: for a propagation candidate (`source_rule_id` starting `PROP-`)
  // the decision is POSTed to the scope's own `propagation_reviews` metadata
  // namespace, which a regenerate cannot erase (scope_merge.py:35-39). For
  // every other line the tint is still local — gap-finder candidates have a
  // different lifecycle and letting them into this namespace would corrupt the
  // "acceptance rate per propagation rule" the task row asks for.
  const [statuses, setStatuses] = useState<Record<string, LineItemStatus>>({});
  const [reviewError, setReviewError] = useState<string | null>(null);

  // Seed the tints from the stored ledger, so a reload shows what the reviewer
  // already decided. Keyed on the five-tuple, never the row index: a room added
  // or reordered would make an index-keyed record point at a different line.
  useEffect(() => {
    const source = scopeData || fetchedScope;
    if (!source) return;
    const ledger = readLedger(source);
    if (Object.keys(ledger).length === 0) return;
    const seeded: Record<string, LineItemStatus> = {};
    source.rooms.forEach((room, roomIndex) => {
      room.items.forEach((item, index) => {
        const decision = ledger[reviewKey(item, room.room_name)];
        if (decision) seeded[`room-${roomIndex}-${index}`] = decision;
      });
    });
    (source.general_conditions || []).forEach((item, index) => {
      const decision = ledger[reviewKey(item, 'General')];
      if (decision) seeded[`general-${index}`] = decision;
    });
    setStatuses(seeded);
  }, [scopeData, fetchedScope]);

  const handleStatusChange = (key: string, status: LineItemStatus) => {
    setStatuses((prev) => ({ ...prev, [key]: status }));

    const item = itemForKey(data, key);
    // 'pending' is the un-reviewed default and is never a decision to record.
    if (status === 'pending') return;
    if (!item || !item.source_rule_id?.startsWith('PROP-') || !projectId || !scopeId) return;

    const [cat, sel] = codeHalves(item);
    setReviewError(null);
    PropagationReviewsAPI.record(projectId, scopeId, {
      rule: item.source_rule_id,
      target_room: roomForKey(data, key),
      target_surface: item.surface || '',
      cat,
      sel,
      decision: status,
    }).catch((err) => {
      console.error('[ScopeReview] failed to record propagation review:', err);
      setReviewError('Could not record that decision. It is not saved.');
    });
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center text-sm font-medium text-muted-foreground">
        <Link to="/" className="hover:text-primary">Dashboard</Link>
        <ChevronRight className="mx-2 h-4 w-4" />
        <Link to="/scope/builder" className="hover:text-primary">Scope Builder</Link>
        <ChevronRight className="mx-2 h-4 w-4" />
        <span className="text-foreground">Review</span>
      </nav>

      {/* Page header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <ClipboardList className="h-6 w-6 text-primary" />
            Scope Review
          </h2>
          <p className="text-muted-foreground">
            Review generated scope items and verify confidence levels.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setActivePanel(activePanel === 'validation' ? null : 'validation')}
            className={cn(
              'rounded-md border px-3 py-2 text-sm font-medium transition-colors',
              activePanel === 'validation' ? 'border-primary bg-primary/10 text-primary' : 'hover:bg-slate-50'
            )}
          >
            Validation ({data.validation_issues.length})
          </button>
          <button
            onClick={() => setActivePanel(activePanel === 'codes' ? null : 'codes')}
            className={cn(
              'rounded-md border px-3 py-2 text-sm font-medium transition-colors',
              activePanel === 'codes' ? 'border-primary bg-primary/10 text-primary' : 'hover:bg-slate-50'
            )}
          >
            Code Reqs ({data.code_requirements.length})
          </button>
          <button
            onClick={() => setShowSources(!showSources)}
            className={cn(
              'rounded-md border px-3 py-2 text-sm font-medium transition-colors',
              showSources
                ? 'bg-indigo-100 text-indigo-800 border-indigo-300'
                : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200'
            )}
          >
            {showSources ? 'Hide Sources' : 'Show Sources'}
          </button>
          <DownloadEsxButton
            scopeId={scopeId}
            projectId={projectId}
            scopeData={hasData ? (data as unknown as object) : null}
            onSaved={() => setSaveBanner('Scope saved to project.')}
            onResult={(filename, report) => {
              setEsxError(null);
              setEsxResult({ filename, report });
            }}
            onError={(message) => {
              setEsxResult(null);
              setEsxError(message);
            }}
          />
        </div>
      </div>

      {/* ESX export banners */}
      {saveBanner && (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
          {saveBanner}
        </div>
      )}
      {esxResult && (
        <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
          Exported <span className="font-medium">{esxResult.filename}</span>
          {' — '}
          {esxResult.report.manual_entry_required.length} item(s) need manual code entry,{' '}
          {esxResult.report.codes_not_in_catalog.length} unverified code(s).
        </div>
      )}
      {esxError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          ESX export failed: {esxError}
        </div>
      )}
      {reviewError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {reviewError}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-3 text-muted-foreground">Loading scope...</span>
        </div>
      )}

      {/* Fetch error */}
      {fetchError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {fetchError}
        </div>
      )}

      {/* Empty state */}
      {!loading && !fetchError && !hasData && (
        <div className="rounded-lg border bg-white p-12 text-center">
          <ClipboardList className="mx-auto h-12 w-12 text-muted-foreground/50" />
          <h3 className="mt-4 text-lg font-medium text-foreground">No scope generated yet</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Generate a scope in the Scope Builder to see results here.
          </p>
          <Link
            to={projectId ? `/scope/builder?project=${projectId}` : '/scope/builder'}
            className="mt-4 inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
          >
            Go to Scope Builder
          </Link>
        </div>
      )}

      {/* Scope content (read-only) */}
      {!loading && hasData && (
        <>
          {/* Summary stats (read-only — no accepted/rejected counts) */}
          <SummaryStatsBar
            rooms={data.rooms}
            generalItems={data.general_conditions}
            statuses={statuses}
            stats={data.stats}
          />

          {/* Questions */}
          {data.questions.length > 0 && (
            <QuestionsSection questions={data.questions} />
          )}

          {/* Warnings */}
          {data.warnings.length > 0 && (
            <WarningsSection warnings={data.warnings} />
          )}

          {/* Main content area with optional side panel */}
          <div className={cn('flex gap-6', activePanel ? 'flex-col lg:flex-row' : '')}>
            {/* Rooms */}
            <div className={cn('space-y-4', activePanel ? 'flex-1' : 'w-full')}>
              {data.rooms.map((room, index) => (
                <RoomSection
                  key={index}
                  room={room}
                  roomIndex={index}
                  statuses={statuses}
                  onStatusChange={handleStatusChange}
                  showSources={showSources}
                />
              ))}

              {/* General conditions */}
              {data.general_conditions.length > 0 && (
                <div className="rounded-lg border bg-white shadow-sm">
                  <div className="p-4">
                    <h3 className="text-lg font-semibold text-foreground">General Conditions</h3>
                  </div>
                  <div className="border-t">
                    <div className="grid grid-cols-12 gap-2 border-b bg-slate-50 px-3 py-2 text-xs font-medium text-muted-foreground">
                      <div className="col-span-2 lg:col-span-1">Code</div>
                      <div className="col-span-4 lg:col-span-4">Description</div>
                      <div className="hidden lg:col-span-1 lg:block">Activity</div>
                      <div className="col-span-2 lg:col-span-2 text-right">Qty</div>
                      <div className="col-span-2 lg:col-span-2">Confidence</div>
                      <div className="col-span-2 lg:col-span-2 text-right">Actions</div>
                    </div>
                    {data.general_conditions.map((item, index) => (
                      <ScopeLineItemRow
                        key={index}
                        item={item}
                        index={index}
                        roomKey="general"
                        status={statuses[`general-${index}`] || 'pending'}
                        onStatusChange={handleStatusChange}
                        showSources={showSources}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Side panel */}
            {activePanel && (
              <div className="w-full lg:w-96 space-y-4">
                {activePanel === 'validation' && (
                  <ValidationPanel issues={data.validation_issues} />
                )}
                {activePanel === 'codes' && (
                  <CodeRequirementsPanel requirements={data.code_requirements} />
                )}
              </div>
            )}
          </div>

          {/* PRP-003 — Why This Scope? panel */}
          <EstimateRuleTracePanel
            trace={trace}
            loading={traceLoading}
            error={traceError}
          />

          {/* Pipeline execution stats */}
          {data.stats?.execution_times && (
            <div className="rounded-lg border bg-white p-4 shadow-sm">
              <p className="text-sm font-medium text-muted-foreground">Pipeline Execution</p>
              <div className="mt-2 flex flex-wrap gap-4 text-xs text-muted-foreground">
                {Object.entries(data.stats.execution_times as Record<string, number>).map(([step, time]) => (
                  <span key={step}>{step}: {(time as number).toFixed(1)}s</span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Returns an empty ScopeGenerationResponse for initial state.
 */
function getEmptyData(): ScopeGenerationResponse {
  return {
    rooms: [],
    general_conditions: [],
    code_requirements: [],
    validation_issues: [],
    questions: [],
    warnings: [],
    metadata: {},
    stats: {},
  };
}

// ================================================================= //
//          T-0403 — the propagation review ledger                     //
// ================================================================= //
// The identity of a reviewed candidate is the five-tuple
// (rule, room, surface, cat, sel) — the same key
// `app/api/routes/propagation_reviews.py::review_key` builds, because a
// decision has to survive a regeneration that renumbers every row.

/** Cat and Sel halves of a line, falling back to splitting the code. */
export function codeHalves(item: ScopeResponseLineItem): [string, string] {
  if (item.cat && item.sel) return [item.cat, item.sel];
  const parts = (item.xactimate_code || '').trim().split(/\s+/);
  return [parts[0] || '', parts[1] || ''];
}

/** The stable key for one propagation candidate, or '' when it is not one. */
export function reviewKey(item: ScopeResponseLineItem, roomName: string): string {
  if (!item.source_rule_id?.startsWith('PROP-')) return '';
  const [cat, sel] = codeHalves(item);
  return [
    item.source_rule_id.trim(),
    (roomName || '').trim().toLowerCase(),
    (item.surface || '').trim().toLowerCase(),
    cat.trim().toUpperCase(),
    sel.trim().toUpperCase(),
  ].join('|');
}

/** key -> decision, read off the stored `propagation_reviews` namespace. */
export function readLedger(scope: ScopeGenerationResponse): Record<string, LineItemStatus> {
  const rows = (scope.metadata?.propagation_reviews || []) as Array<Record<string, unknown>>;
  const out: Record<string, LineItemStatus> = {};
  if (!Array.isArray(rows)) return out;
  rows.forEach((row) => {
    const key = typeof row?.key === 'string' ? row.key : '';
    const decision = row?.decision as LineItemStatus | undefined;
    if (key && decision) out[key] = decision;
  });
  return out;
}

/** The line item a `room-<i>-<j>` / `general-<j>` component key points at. */
export function itemForKey(
  scope: ScopeGenerationResponse,
  key: string,
): ScopeResponseLineItem | null {
  const general = key.match(/^general-(\d+)$/);
  if (general) return scope.general_conditions?.[Number(general[1])] ?? null;
  const room = key.match(/^room-(\d+)-(\d+)$/);
  if (!room) return null;
  return scope.rooms?.[Number(room[1])]?.items?.[Number(room[2])] ?? null;
}

/** The room name a component key belongs to — the target_room of the tuple. */
export function roomForKey(scope: ScopeGenerationResponse, key: string): string {
  const room = key.match(/^room-(\d+)-\d+$/);
  if (!room) return 'General';
  return scope.rooms?.[Number(room[1])]?.room_name || '';
}
